import mermaid from 'mermaid';
import React, { useEffect, useRef, useState } from 'react';

let mermaidThemeMode: 'light' | 'dark' | null = null;
const ORANGE_BORDER = '#FF6B35';
const CLUSTER_BG_LIGHT = '#FFF4EC';
const CLUSTER_BG_DARK = '#3B2A22';

function initMermaid(isDark: boolean) {
  const nextMode: 'light' | 'dark' = isDark ? 'dark' : 'light';
  if (mermaidThemeMode === nextMode) return;

  mermaid.initialize({
    startOnLoad: false,
    theme: 'base',
    securityLevel: 'loose',
    fontFamily: 'Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
    themeVariables: isDark
      ? {
          primaryColor: '#1f2937',
          primaryTextColor: '#f3f4f6',
          primaryBorderColor: '#374151',
          tertiaryBorderColor: '#FF6B35',
          clusterBorder: '#FF6B35',
          lineColor: '#9ca3af',
          secondaryColor: '#111827',
          tertiaryColor: '#1f2937',
          mainBkg: '#111827',
          nodeBkg: '#1f2937',
          clusterBkg: CLUSTER_BG_DARK,
          edgeLabelBackground: '#0f172a',
          textColor: '#f3f4f6',
          actorBorder: '#4b5563',
          actorBkg: '#111827',
          actorTextColor: '#f3f4f6',
          labelTextColor: '#e5e7eb',
          noteBkgColor: '#1e293b',
          noteTextColor: '#e5e7eb',
          noteBorderColor: '#475569',
        }
      : {
          primaryColor: '#ffffff',
          primaryTextColor: '#0f172a',
          primaryBorderColor: '#cbd5e1',
          tertiaryBorderColor: '#FF6B35',
          clusterBorder: '#FF6B35',
          lineColor: '#334155',
          secondaryColor: '#f8fafc',
          tertiaryColor: '#eef2ff',
          mainBkg: '#ffffff',
          nodeBkg: '#ffffff',
          clusterBkg: CLUSTER_BG_LIGHT,
          edgeLabelBackground: '#f8fafc',
          textColor: '#0f172a',
          actorBorder: '#94a3b8',
          actorBkg: '#ffffff',
          actorTextColor: '#0f172a',
          labelTextColor: '#0f172a',
          noteBkgColor: '#fff7ed',
          noteTextColor: '#7c2d12',
          noteBorderColor: '#fdba74',
        },
  });
  mermaidThemeMode = nextMode;
}

function parseNumeric(value: string | null): number | null {
  if (!value) return null;
  const match = value.match(/-?\d+(\.\d+)?/);
  if (!match) return null;
  const parsed = Number(match[0]);
  return Number.isFinite(parsed) ? parsed : null;
}

function scaleStrokeWidth(value: string, factor: number): string {
  const parsed = parseNumeric(value);
  if (parsed == null) return value;
  return `${(parsed * factor).toFixed(2).replace(/\.00$/, '')}`;
}

function boostStrokeWidth(element: Element, factor: number) {
  const stroke = element.getAttribute('stroke');
  const hasStroke =
    Boolean(stroke && stroke.trim() && stroke.trim().toLowerCase() !== 'none') ||
    /stroke\s*:/.test(element.getAttribute('style') || '');
  if (!hasStroke) return;

  const widthAttr = element.getAttribute('stroke-width');
  if (widthAttr) {
    element.setAttribute('stroke-width', scaleStrokeWidth(widthAttr, factor));
  } else {
    element.setAttribute('stroke-width', `${factor}`);
  }

  const style = element.getAttribute('style');
  if (style && /stroke-width\s*:/.test(style)) {
    element.setAttribute(
      'style',
      style.replace(
        /stroke-width\s*:\s*([^;]+)/gi,
        (_, rawWidth) => `stroke-width:${scaleStrokeWidth(rawWidth, factor)}`
      )
    );
  }
}

function estimateTitleMetrics(title: Element): {
  fontSize: number;
  lineHeight: number;
  lineCount: number;
  width: number;
  height: number;
} {
  const rawFontSize = parseNumeric(title.getAttribute('font-size')) || 14;
  const fontSize = Math.max(12, rawFontSize);
  const lineHeight = fontSize * 1.2;
  const tspans = title.querySelectorAll('tspan');
  const textContent = (title.textContent || '').trim();
  const lineCount = Math.max(1, tspans.length || textContent.split('\n').length);
  const longestLineLength = Math.max(
    1,
    ...textContent.split('\n').map(line => line.trim().length)
  );
  const width = longestLineLength * fontSize * 0.58;
  const height = lineCount * lineHeight;
  return { fontSize, lineHeight, lineCount, width, height };
}

function injectTitleBackground(title: Element, width: number, height: number, isDark: boolean) {
  const parent = title.parentElement;
  if (!parent) return;

  const x = parseNumeric(title.getAttribute('x')) || 0;
  const y = parseNumeric(title.getAttribute('y')) || 0;
  const padX = 10;
  const padY = 5;
  const backgroundX = x - width / 2 - padX;
  const backgroundY = y - height + padY * 0.5;

  const existing = parent.querySelector(':scope > rect[data-mermaid-cluster-title-bg="1"]');
  const bgRect =
    existing ??
    (() => {
      const created = title.ownerDocument.createElementNS(
        'http://www.w3.org/2000/svg',
        'rect'
      );
      created.setAttribute('data-mermaid-cluster-title-bg', '1');
      parent.insertBefore(created, title);
      return created;
    })();

  bgRect.setAttribute('x', `${backgroundX.toFixed(2)}`);
  bgRect.setAttribute('y', `${backgroundY.toFixed(2)}`);
  bgRect.setAttribute('width', `${(width + padX * 2).toFixed(2)}`);
  bgRect.setAttribute('height', `${(height + padY * 1.5).toFixed(2)}`);
  bgRect.setAttribute('rx', '10');
  bgRect.setAttribute('ry', '10');
  bgRect.setAttribute('fill', isDark ? CLUSTER_BG_DARK : CLUSTER_BG_LIGHT);
  bgRect.setAttribute('stroke', ORANGE_BORDER);
  bgRect.setAttribute('stroke-width', '1.5');
}

function moveClusterTitlesAboveEdges(svgRoot: Element, isDark: boolean) {
  const minimumTopY = 12;

  svgRoot.querySelectorAll('g.cluster').forEach(cluster => {
    const rect = cluster.querySelector('rect');
    const title = cluster.querySelector('text');
    if (!rect || !title) return;

    const rectY = parseNumeric(rect.getAttribute('y'));
    if (rectY == null) return;

    const { fontSize, height, lineHeight } = estimateTitleMetrics(title);
    const outerGap = Math.max(12, fontSize * 0.75);
    const requiredTopSpace = height + outerGap;
    const hasSpaceAbove = rectY - requiredTopSpace > minimumTopY;

    // Prefer above the cluster when there is room; otherwise move inside top padding.
    const baselineY = hasSpaceAbove
      ? rectY - outerGap
      : rectY + Math.max(14, lineHeight * 0.95);

    title.setAttribute('y', `${Math.max(minimumTopY, baselineY).toFixed(2)}`);
    title.setAttribute('dy', '0');
    title.setAttribute('font-weight', '600');
    injectTitleBackground(title, estimateTitleMetrics(title).width, height, isDark);
  });

  // Some Mermaid renderers place cluster titles outside `g.cluster`.
  svgRoot.querySelectorAll('.cluster-label text, .clusterLabel text').forEach(title => {
    const currentY = parseNumeric(title.getAttribute('y')) || minimumTopY + 12;
    const { fontSize, height, width } = estimateTitleMetrics(title);
    const nextY = Math.max(minimumTopY + fontSize, currentY - Math.max(10, fontSize * 0.7));
    title.setAttribute('y', `${nextY.toFixed(2)}`);
    title.setAttribute('dy', '0');
    title.setAttribute('font-weight', '600');
    injectTitleBackground(title, width, height, isDark);
  });
}

function stylizeMermaidSvg(svg: string, isDark: boolean): string {
  const parser = new DOMParser();
  const svgDoc = parser.parseFromString(svg, 'image/svg+xml');
  const svgRoot = svgDoc.documentElement;
  if (!svgRoot || svgRoot.tagName.toLowerCase() !== 'svg') {
    return svg;
  }

  // Cluster/subgraph: fundo laranja suave + borda laranja.
  const clusterRects = svgRoot.querySelectorAll(
    '.cluster rect, g.cluster rect, rect.cluster, .clusterRect, .cluster rect'
  );
  clusterRects.forEach(rect => {
    rect.setAttribute('fill', isDark ? CLUSTER_BG_DARK : CLUSTER_BG_LIGHT);
    rect.setAttribute('stroke', ORANGE_BORDER);
  });
  moveClusterTitlesAboveEdges(svgRoot, isDark);

  // Tudo com borda/linha: 50% mais grosso.
  svgRoot
    .querySelectorAll(
      'path, line, polyline, polygon, rect, circle, ellipse, .edgePath .path, .flowchart-link'
    )
    .forEach(element => boostStrokeWidth(element, 1.5));

  // Arredondar todos os elementos com canto.
  svgRoot.querySelectorAll('rect').forEach(rect => {
    const currentRx = parseNumeric(rect.getAttribute('rx')) || 0;
    const currentRy = parseNumeric(rect.getAttribute('ry')) || 0;
    const rounded = Math.max(12, currentRx, currentRy);
    rect.setAttribute('rx', `${rounded}`);
    rect.setAttribute('ry', `${rounded}`);
  });

  // Deixar cantos/terminações de linhas mais suaves.
  svgRoot.querySelectorAll('path, polyline, polygon, line').forEach(element => {
    element.setAttribute('stroke-linecap', 'round');
    element.setAttribute('stroke-linejoin', 'round');
  });

  return new XMLSerializer().serializeToString(svgRoot);
}

interface MermaidBlockProps {
  chart: string;
}

export const MermaidBlock: React.FC<MermaidBlockProps> = ({ chart }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [isDark, setIsDark] = useState<boolean>(() =>
    document.documentElement.classList.contains('dark')
  );

  useEffect(() => {
    const root = document.documentElement;
    const observer = new MutationObserver(() => {
      setIsDark(root.classList.contains('dark'));
    });
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    initMermaid(isDark);

    if (!containerRef.current || !chart.trim()) return;

    const id = `mermaid-${Math.random().toString(36).slice(2, 10)}`;

    setError(null);

    mermaid
      .render(id, chart.trim())
      .then(({ svg }) => {
        if (containerRef.current) {
          containerRef.current.innerHTML = stylizeMermaidSvg(svg, isDark);
        }
      })
      .catch(() => {
        setError('Não foi possível renderizar o diagrama.');
      });
  }, [chart, isDark]);

  if (error) {
    return (
      <div className="my-4 rounded-md border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-600 dark:text-red-400">
        {error}
      </div>
    );
  }

  return (
    <div className="my-4 overflow-hidden rounded-xl border border-gray-200/90 dark:border-gray-700/90 bg-gray-50/70 dark:bg-gray-900/70 shadow-sm">
      <div className="border-b border-gray-200/80 dark:border-gray-700/80 px-4 py-2 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
        Diagrama Mermaid
      </div>
      <div
        ref={containerRef}
        className="flex justify-center overflow-x-auto px-3 py-4 sm:px-4 [&>svg]:max-w-full [&>svg]:h-auto [&_text]:font-[Inter,_system-ui,_-apple-system,_Segoe_UI,_Roboto,_sans-serif]"
      />
    </div>
  );
};
